import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Paper, Stack, Typography } from '@mui/material'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useDeletePolicyMutation, useGetPolicyQuery, useUpdatePolicyMutation } from '@/core/services/policy'
import { Role } from '@/core/types/shared'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import PolicyForm from './components/PolicyForm'

export default function PolicyPage() {
  const { policyId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: policy, isLoading } = useGetPolicyQuery(policyId ?? '', { skip: !policyId })
  const [updatePolicy, { isLoading: isUpdatingPolicy }] = useUpdatePolicyMutation()
  const [deletePolicy, { isLoading: isDeletingPolicy }] = useDeletePolicyMutation()

  const [editable, setEditable] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const initialValues = useMemo(
    () =>
      policy
        ? {
          name: policy.name,
          description: policy.description,
          permissions: policy.permissions,
        }
        : undefined,
    [policy]
  )

  if (isLoading) {
    return null
  }

  if (!policy) return null

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{policy.name}</Typography>
          {(authUser?.roles.includes(Role.SuperAdmin) || policy.createdBy === authUser?._id) && (
            <Stack direction="row" spacing={2}>
              <Button color="error" onClick={() => setOpenDeleteConfirmationDialog(true)} variant="contained">
                {t('common:button.delete.label')}
              </Button>
              <Button onClick={() => setEditable(!editable)} variant="contained">
                {!editable ? t('common:button.modify.label') : t('common:button.cancel.label')}
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack spacing={3}>
          <Paper>
            <Box p={3}>
              <PolicyForm
                key={editable ? 'editable' : 'readOnly'}
                initialValues={initialValues}
                isSubmitting={isUpdatingPolicy}
                onSubmit={async (values) => {
                  try {
                    await updatePolicy({ id: policy._id, ...values }).unwrap()
                    setEditable(false)
                  } catch (error) {
                    enqueueSnackbar(getApiErrorMessage(error, t('notification.policy.update.error')), { variant: 'error' })
                  }
                }}
                readOnly={!editable}
              />
            </Box>
          </Paper>
        </Stack>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingPolicy}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          await deletePolicy(policy._id).unwrap()
          setOpenDeleteConfirmationDialog(false)
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.policy.title')}
        message={t('dialog.deleteConfirmation.policy.message')}
      />
    </PageContainer>
  )
}
